/**
 * Everything that touches money: the payment link, recording a payment by
 * hand, a member paying for themselves, and the club's own bank/UPI details.
 *
 * Receipt numbers are allocated by the API inside a transaction and never
 * derived in a component — two admins recording payments at the same moment
 * must not both mint TRC/2026-27/0007.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  bibNo,
  clubCanCollect,
  clubFees,
  feeFor,
  feeKindFor,
  feesError,
  formatDate,
  formatMoney,
  memberFee,
  TIERS,
  today,
} from '../../data';
import { previewCoversTill, useClub, useFees } from '../../context/ClubContext';
import { useUI } from '../../context/UIContext';
import { pickAndResizePhoto, QR_IMAGE } from '../../lib/image';
import { PaymentPanel } from '../PaymentPanel';
import {
  Button,
  Field,
  FormError,
  Hint,
  Row2,
  SheetActions,
  SheetHead,
  TEXTAREA_CLS,
} from '../ui';

/**
 * The Razorpay link, with the WhatsApp message the committee will actually
 * send. Seeing the message before it goes out catches a wrong name or fee.
 */
export function PayLinkSheet({ member }) {
  const { markLinkSent } = useClub();
  const { closeSheet, toast } = useUI();
  const navigate = useNavigate();

  /* A link to a club that has published no account tells the member what they
     owe and gives them nowhere to send it. Say so HERE — the moment the link
     is made — rather than letting the committee find out from the member.
     Optional chaining like useFees(): a sheet can render before db lands, and
     an unknown setup must not raise a false alarm. */
  const meta = useClub().db?.meta ?? null;
  const canCollect = meta ? clubCanCollect(meta) : true;

  /*
   * The URL cannot be built here any more.
   *
   * It used to be `/pay/${member.id}`, which meant a public page had to resolve
   * a member id — and a public page that resolves member ids lets anyone walk
   * them and read the roster. So the API mints a one-time token and hands back
   * the URL, which is why this sheet has a loading state where it used to have
   * a string.
   *
   * Creating the link is also what marks the member chased, so it happens once,
   * when the sheet opens. Re-opening the sheet issues a fresh link and kills
   * the old one — which is what you want when a member says they lost the
   * message.
   */
  const [link, setLink] = useState(null);
  const [linkError, setLinkError] = useState(null);
  const [copied, setCopied] = useState(false);
  // The CLUB's amount for THIS member — joining if they have never paid,
  // renewal otherwise. The link the server mints carries the same number.
  const fees = useFees();
  const fee = link?.amount ?? memberFee(fees, member);

  useEffect(() => {
    let cancelled = false;
    markLinkSent(member.id)
      .then((created) => {
        if (!cancelled) setLink(created);
      })
      .catch((err) => {
        if (!cancelled) setLinkError(err?.message ?? 'Could not create the payment link.');
      });
    return () => {
      cancelled = true;
    };
  }, [member.id, markLinkSent]);

  const url = link?.url ?? '';
  const message =
    link?.whatsappMessage ??
    `Vanakkam ${member.name.split(' ')[0]}! Your Tirupur Runners membership (Bib ${bibNo(
      member.bib,
    )}) is due — ₹${fee} for the year.`;

  async function copy(text, what) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast(`${what} copied`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is blocked outside a secure context, and on an office
      // desktop over plain http that is the normal case, not an edge one.
      toast('Could not copy — select the link and copy it by hand');
    }
  }

  function send() {
    // The member was marked chased when the link was created; this button is
    // the committee confirming they have actually sent it on.
    closeSheet();
    toast(`Payment link sent to ${member.name.split(' ')[0]}`);
    /*
     * A `pending` member is one the committee just added: they are NOT on the
     * roster yet, they are waiting in Membership requests to be activated once
     * the money is in. Sending the link is the first half of that job, so land
     * the admin on the queue that holds the second half — otherwise the member
     * they just created is nowhere on the screen they are looking at.
     *
     * An ACTIVE member being chased for a renewal has no row in Requests, so
     * they are left where they are (owner's direction, 2 Sep 2026).
     */
    if (member.status === 'pending') navigate('/a/requests');
  }

  return (
    <>
      <SheetHead
        title="Send payment link"
        sub={`Bib ${bibNo(member.bib)} · ${member.name} · ${TIERS[member.tier].label} ₹${fee}${
          feeKindFor(member) === 'join' ? ' (joining)' : ''
        }`}
      />

      {!canCollect && (
        <div className="mb-3 border-l-2 border-gold-500 bg-gold-50 p-3.5">
          <div className="font-market text-[13px] font-extrabold tracking-[-0.02em] text-ink-700">
            This link has nowhere to pay
          </div>
          <p className="mt-1 text-[12px] leading-relaxed text-copy-muted">
            The club has published no UPI id, account or QR, so {member.name.split(' ')[0]} will
            see the amount and no way to send it. Add the details and the same link starts
            working — you do not need to reissue it.
          </p>
          <button
            type="button"
            className="mt-2 border border-line-strong bg-white px-3 py-1.5 font-market text-[10px] font-bold uppercase tracking-[0.1em] text-ink-700 transition-colors duration-200 hover:border-ink-700"
            onClick={() => {
              closeSheet();
              navigate('/a/settings');
            }}
          >
            Set up payments
          </button>
        </div>
      )}

      <div className="border border-dashed border-line-strong bg-ground p-3.5">
        <div className="font-market text-[9px] uppercase tracking-[0.1em] text-copy-muted">
          Payment link
        </div>
        <div className="mt-2 flex items-stretch gap-2">
          <div className="min-w-0 flex-1 break-all border border-line bg-white px-2.5 py-2 font-market text-[10.5px] text-gold-800">
            {url || (linkError ? '—' : 'Creating a one-time link…')}
          </div>
          <button
            type="button"
            className={`flex flex-none items-center gap-1.5 border px-3 font-market text-[10px] uppercase tracking-wide transition-all duration-200 ${
              copied
                ? 'border-sea-300 bg-sea-50 text-sea-600'
                : 'border-line bg-white text-copy-muted hover:border-ink-700 hover:text-ink-700'
            }`}
            disabled={!url}
            onClick={() => void copy(url, 'Link')}
          >
            {copied ? (
              <>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                Copied
              </>
            ) : (
              <>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect x="9" y="9" width="12" height="12" rx="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                Copy
              </>
            )}
          </button>
        </div>
        <Hint style={{ marginTop: 9 }}>
          {canCollect
            ? 'Opens a page showing what membership covers, then pays by UPI, card or netbanking. Their dues date updates automatically once it clears.'
            : 'Opens a page showing what membership covers and what they owe. Until the club publishes an account, it cannot take the money.'}
        </Hint>
      </div>

      <div className="mt-3 break-words border border-sea-300 bg-sea-50 p-3.5 text-[12.5px] leading-relaxed text-sea-600 [overflow-wrap:anywhere]">
        <div className="mb-1.5 flex items-center gap-2.5 font-market text-[9px] uppercase tracking-[0.1em] text-sea-600">
          WhatsApp message preview
          <button
            type="button"
            className="ml-auto text-sea-600 underline underline-offset-2 hover:text-sea-600"
            onClick={() => void copy(message, 'Message')}
          >
            Copy message
          </button>
        </div>
        {message}
      </div>

      <Hint style={{ marginTop: 12 }}>
        Sending to <b>{member.phone || 'no number on file'}</b>
      </Hint>

      {linkError && <FormError>{linkError}</FormError>}

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Cancel
        </Button>
        <Button tone="p" onClick={send} disabled={!url}>
          Send on WhatsApp
        </Button>
      </SheetActions>
    </>
  );
}

/** Admin records what actually arrived by cash, UPI or bank transfer. */
export function RecordPaymentSheet({ member }) {
  const { recordPayment } = useClub();
  const { closeSheet, toast } = useUI();
  const tier = TIERS[member.tier];
  const fees = useFees();

  // Prefilled with what THIS member owes under the club's fees (joining for
  // the never-paid, renewal otherwise) — still editable: part payments happen.
  const [amount, setAmount] = useState(String(memberFee(fees, member)));
  const [method, setMethod] = useState('cash'); // 'cash' | 'upi' | 'bank' | 'razorpay'
  const [paidOn, setPaidOn] = useState(today());
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Same date the write will use. Clearing the date field left `paidOn` as ''
  // and the hint printed "Invalid Date" while the save silently used today().
  const coversTill = previewCoversTill(member, paidOn || today());

  async function save() {
    const value = Number.parseFloat(amount);
    if (!value || value <= 0) return setError('Enter a valid amount.');
    if (!paidOn) return setError('Enter the date the money arrived.');

    setSaving(true);
    try {
      const receipt = await recordPayment(member.id, value, method, paidOn);
      closeSheet();
      toast(`Payment recorded · ${receipt}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record that payment.');
      setSaving(false);
    }
  }

  return (
    <>
      <SheetHead
        title="Record payment"
        sub={`Bib ${bibNo(member.bib)} · ${member.name} · ${tier.label}`}
      />

      <Row2>
        <Field label="Amount ₹" required>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Method">
          <select value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="cash">Cash</option>
            <option value="upi">UPI (direct)</option>
            <option value="bank">Bank transfer</option>
          </select>
        </Field>
      </Row2>

      <Field label="Paid on">
        <input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
      </Field>

      <Hint>
        {coversTill ? (
          <>
            Extends membership to <b>{formatDate(coversTill)}</b>.
          </>
        ) : (
          'Life membership — no expiry.'
        )}{' '}
        Receipt number generated automatically.
      </Hint>

      <FormError>{error}</FormError>

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Cancel
        </Button>
        <Button tone="p" onClick={save} disabled={saving}>
          {/* The amount being recorded, not the tier fee — a ₹750 part
              payment used to sit under a button reading "Record ₹1500". */}
          Record {formatMoney(Number.parseFloat(amount) || 0)}
        </Button>
      </SheetActions>
    </>
  );
}

/**
 * The joining fee that arrived as cash, not on the link.
 *
 * A request is stuck at "Link sent · unpaid" until something marks it paid,
 * and Activate refuses anything else. The committee approved this person and
 * then took ₹750 from them at the ground on Sunday; this is where that gets
 * written down, with a real receipt number, so the pipeline can finish.
 *
 * The member's link stays live afterwards — it simply stops asking for money
 * and asks them to set a password instead.
 */
export function RecordJoinCashSheet({ request }) {
  const { recordJoinCash } = useClub();
  const { closeSheet, toast } = useUI();
  const fees = useFees();
  const tier = TIERS[request.tier] ?? TIERS.annual;

  // What the link would have charged: the club's JOINING fee for this tier.
  // Editable, because cash is rounded and part payments happen.
  const [amount, setAmount] = useState(
    String(request.amount ?? feeFor(fees, request.tier ?? 'annual', 'join')),
  );
  const [method, setMethod] = useState('cash'); // 'cash' | 'upi' | 'bank'
  const [paidOn, setPaidOn] = useState(today());
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    const value = Number.parseFloat(amount);
    if (!value || value <= 0) return setError('Enter the amount received.');
    if (!paidOn) return setError('Enter the date the money arrived.');

    setSaving(true);
    try {
      const receipt = await recordJoinCash(request.id, { amount: value, method, date: paidOn });
      closeSheet();
      toast(`Joining fee recorded · ${receipt}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record that payment.');
      setSaving(false);
    }
  }

  return (
    <>
      <SheetHead
        title="Record joining fee"
        sub={`${request.name} · ${tier.label} · paid outside the link`}
      />

      <Row2>
        <Field label="Amount ₹" required>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Method">
          <select value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="cash">Cash</option>
            <option value="upi">UPI (direct)</option>
            <option value="bank">Bank transfer</option>
          </select>
        </Field>
      </Row2>

      <Field label="Paid on">
        <input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
      </Field>

      <Hint>
        Marks this request <b>paid</b> and generates the receipt, so you can activate the
        membership. The payment link already sent stays valid — it will ask {request.name.split(' ')[0]}{' '}
        to set a password instead of paying.
      </Hint>

      <FormError>{error}</FormError>

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Cancel
        </Button>
        <Button tone="p" onClick={save} disabled={saving}>
          Record {formatMoney(Number.parseFloat(amount) || 0)}
        </Button>
      </SheetActions>
    </>
  );
}

/**
 * A member paying their own dues from the Payments or My Bib screen.
 *
 * This goes through `payOwnDues`, not `recordPayment`. Recording a payment is
 * the committee entering money that arrived by cash or UPI and is admin-only;
 * this endpoint takes no amount and no date, so there is nothing a member can
 * inflate or backdate.
 */
export function SelfPaySheet({ member }) {
  const { db, payOwnDues } = useClub();
  const { closeSheet, toast } = useUI();
  // The club's amount for this member — the server charges the same number
  // from the same rule, so what the button says is what the receipt will say.
  const fee = memberFee(useFees(), member);
  const meta = db?.meta ?? {};
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);
  const [proof, setProof] = useState(null);

  /*
   * This RECORDS a payment; it does not take one.
   *
   * The sheet used to say "You'll be taken to Razorpay" over a button that
   * called the API and marked the member paid for a year on the spot — no
   * gateway, no money. So it now shows the club's OWN payment details (the
   * same ones the public /pay link prints), takes the screenshot as proof,
   * and says plainly that it is a confirmation.
   */
  async function pay() {
    setPaying(true);
    setError(null);
    try {
      const receipt = await payOwnDues(proof);
      closeSheet();
      toast(`Payment recorded · ${receipt}`);
    } catch (err) {
      setPaying(false);
      setError(err?.message ?? 'That could not be recorded. Try again.');
    }
  }

  return (
    <>
      <SheetHead
        title="Pay membership"
        sub={`Bib ${bibNo(member.bib)} · ${TIERS[member.tier].label} membership`}
      />

      <PaymentPanel
        amount={fee}
        payMode={meta.payMode}
        upi={meta.bankUpi}
        qrUrl={meta.bankQrUrl}
        holder={meta.bankHolder}
        account={meta.bankAccount}
        ifsc={meta.bankIfsc}
        details={meta.bankDetails}
        bib={member.bib ? bibNo(member.bib) : null}
        proof={proof}
        onProofChange={setProof}
        onPay={pay}
        paying={paying}
        error={error}
        coversTill={previewCoversTill(member) ? formatDate(previewCoversTill(member)) : null}
      />

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Not now
        </Button>
      </SheetActions>
    </>
  );
}

/* ------------------------------------------------------- membership fees -- */

/**
 * The club's own joining and renewal amounts, per tier — committee only.
 *
 * Two numbers because they genuinely differ: many clubs fold a kit or a
 * registration charge into the first payment and renew cheaper. WHO pays
 * which is never chosen here — a member who has never paid owes the joining
 * amount, everyone else the renewal (see data/fees.js). Links already sent
 * keep the amount they were sent with.
 */
export function FeesSheet() {
  const { setFees } = useClub();
  const { closeSheet, toast } = useUI();
  const fees = clubFees(useFees());

  const [form, setForm] = useState(() => {
    const initial = {};
    for (const tier of Object.keys(TIERS)) {
      initial[tier] = { join: String(fees[tier].join), renew: String(fees[tier].renew) };
    }
    return initial;
  });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const set = (tier, kind) => (e) =>
    setForm((f) => ({ ...f, [tier]: { ...f[tier], [kind]: e.target.value } }));

  async function save() {
    setError(null);
    const problem = feesError(form);
    if (problem) return setError(problem);
    setSaving(true);
    try {
      await setFees(form);
      closeSheet();
      toast('Membership fees updated');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
      setSaving(false);
    }
  }

  return (
    <>
      <SheetHead
        title="Membership fees"
        sub="What your club charges — joining first, renewal every year after"
      />

      <div className="border border-line">
        <div className="grid grid-cols-[1fr_92px_92px] gap-x-3 border-b border-line bg-ground px-3.5 py-2 font-market text-[9px] uppercase tracking-[0.12em] text-copy-muted">
          <div>Tier</div>
          <div>Joining ₹</div>
          <div>Renewal ₹</div>
        </div>
        {Object.keys(TIERS).map((tier) => {
          const life = TIERS[tier].months === null;
          return (
            <div
              key={tier}
              className="grid grid-cols-[1fr_92px_92px] items-center gap-x-3 border-b border-line px-3.5 py-2.5 last:border-b-0"
            >
              <div>
                <div className="font-market text-[13px] font-bold text-ink-700">
                  {TIERS[tier].label}
                </div>
                <div className="font-market text-[9.5px] text-copy-muted">
                  {life ? 'One payment, no renewal' : `${TIERS[tier].months} months per payment`}
                </div>
              </div>
              <input
                type="number"
                min="1"
                value={form[tier].join}
                onChange={set(tier, 'join')}
                aria-label={`${TIERS[tier].label} joining amount`}
              />
              {life ? (
                <div className="text-center font-market text-[12px] text-copy-label">—</div>
              ) : (
                <input
                  type="number"
                  min="1"
                  value={form[tier].renew}
                  onChange={set(tier, 'renew')}
                  aria-label={`${TIERS[tier].label} renewal amount`}
                />
              )}
            </div>
          );
        })}
      </div>

      <Hint style={{ marginTop: 12 }}>
        A member who has <b>never paid</b> is charged the joining amount; everyone
        else the renewal. Links already sent keep their amount — resend one to
        use the new figure.
      </Hint>

      <FormError>{error}</FormError>

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Cancel
        </Button>
        <Button tone="p" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save fees'}
        </Button>
      </SheetActions>
    </>
  );
}

/* -------------------------------------------------- club payment details -- */

/**
 * How the club COLLECTS money — the setup every payment and joining link reads.
 *
 * Two modes, the committee's choice:
 *   QR / bank transfer — the member pays the club's QR or account directly
 *     and can attach a payment screenshot as proof.
 *   Razorpay — instant gateway payment; the club enters its own Key ID and
 *     Key Secret from the Razorpay dashboard.
 *
 * The bank block is structured (holder, account number, IFSC) because a
 * member typing a transfer needs fields, not a paragraph — the free-text line
 * survives for the instruction that always rides along ("put your bib in the
 * note"). The QR is resized on the canvas at high quality — it has to survive
 * being READ by a camera. The Razorpay secret is write-only: leave it blank
 * to keep the stored one.
 */
export function PaymentDetailsSheet({ meta }) {
  const { setPaymentDetails } = useClub();
  const { closeSheet, toast } = useUI();

  const [payMode, setPayMode] = useState(meta.payMode ?? 'manual');
  const [details, setDetails] = useState(meta.bankDetails ?? '');
  const [upi, setUpi] = useState(meta.bankUpi ?? '');
  const [holder, setHolder] = useState(meta.bankHolder ?? '');
  const [account, setAccount] = useState(meta.bankAccount ?? '');
  const [ifsc, setIfsc] = useState(meta.bankIfsc ?? '');
  const [rpKeyId, setRpKeyId] = useState(meta.rpKeyId ?? '');
  const [rpKeySecret, setRpKeySecret] = useState('');
  /* Either an existing /api/images URL or a fresh data URL, until saved. */
  const [qr, setQr] = useState(meta.bankQrUrl ?? null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  async function pickQr() {
    try {
      setQr(await pickAndResizePhoto(QR_IMAGE));
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not read that image');
    }
  }

  async function save() {
    setError(null);
    if (payMode === 'razorpay' && !rpKeyId.trim() && !meta.rpConfigured) {
      return setError('Razorpay mode needs the Key ID and Key Secret from your Razorpay dashboard.');
    }
    setSaving(true);
    try {
      await setPaymentDetails({
        details: details.trim(),
        upi: upi.trim(),
        qr,
        payMode,
        bankHolder: holder.trim(),
        bankAccount: account.trim(),
        bankIfsc: ifsc.trim(),
        rpKeyId: rpKeyId.trim(),
        rpKeySecret: rpKeySecret.trim(),
      });
      closeSheet();
      toast('Payment setup updated');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
      setSaving(false);
    }
  }

  const MODES = [
    ['manual', 'QR / bank transfer', 'Member pays you directly and attaches a screenshot'],
    ['razorpay', 'Razorpay', 'Instant payment through your own Razorpay account'],
  ];

  return (
    <>
      <SheetHead
        title="How members pay you"
        sub="Shown on every payment and joining link you send"
      />

      <div className="mb-4">
        <div className="mb-2 font-market text-[10px] font-bold uppercase tracking-[0.16em] text-copy-label">
          Collection mode
        </div>
        <div className="grid grid-cols-2 gap-2">
          {MODES.map(([key, label, sub]) => (
            <button
              key={key}
              type="button"
              onClick={() => setPayMode(key)}
              className={`border p-3 text-left transition ${
                payMode === key
                  ? 'border-gold-500 bg-gold-50 ring-2 ring-gold-500/20'
                  : 'border-line bg-white hover:border-ink-700'
              }`}
            >
              <p className="font-market text-[12.5px] font-bold text-ink-700">{label}</p>
              <p className="mt-0.5 text-[11px] leading-snug text-copy-muted">{sub}</p>
            </button>
          ))}
        </div>
      </div>

      {payMode === 'razorpay' ? (
        <>
          <Row2>
            <Field label="Razorpay Key ID" required>
              <input
                value={rpKeyId}
                onChange={(e) => setRpKeyId(e.target.value)}
                placeholder="rzp_live_…"
              />
            </Field>
            <Field label="Key Secret" required={!meta.rpConfigured}>
              <input
                type="password"
                value={rpKeySecret}
                onChange={(e) => setRpKeySecret(e.target.value)}
                placeholder={meta.rpConfigured ? 'Saved — leave blank to keep' : 'From the dashboard'}
                autoComplete="off"
              />
            </Field>
          </Row2>
          <Hint>
            Both come from your Razorpay dashboard → Settings → API keys. The
            secret is stored server-side and never shown again. Payments land in
            the club&rsquo;s own Razorpay account, and receipts are issued here as
            usual.
          </Hint>
        </>
      ) : (
        <>
          <Field label="UPI id">
            <input
              value={upi}
              onChange={(e) => setUpi(e.target.value)}
              placeholder="tirupurrunners@okicici"
            />
          </Field>

          <Field label="Account holder name">
            <input
              value={holder}
              onChange={(e) => setHolder(e.target.value)}
              placeholder="Tirupur Runners Club"
            />
          </Field>
          <Row2>
            <Field label="Account number">
              <input
                value={account}
                onChange={(e) => setAccount(e.target.value.replace(/\D/g, ''))}
                inputMode="numeric"
                placeholder="123456789012"
              />
            </Field>
            <Field label="IFSC code">
              <input
                value={ifsc}
                onChange={(e) => setIfsc(e.target.value.toUpperCase())}
                placeholder="HDFC0001234"
                maxLength={11}
              />
            </Field>
          </Row2>

          <Field label="Instructions for the member">
            <textarea
              className={TEXTAREA_CLS}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder={'Add your name and bib number in the payment note'}
            />
          </Field>

          <div className="mb-4">
            <div className="mb-2 font-market text-[10px] font-bold uppercase tracking-[0.16em] text-copy-label">
              Payment QR
            </div>
            <div className="flex items-center gap-4 border border-line bg-ground p-3.5">
              {qr ? (
                <img
                  src={qr}
                  alt="Club payment QR"
                  className="h-[92px] w-[92px] flex-none border border-line bg-white object-contain"
                />
              ) : (
                <div className="grid h-[92px] w-[92px] flex-none place-items-center border border-dashed border-line-strong bg-white text-center font-market text-[9.5px] leading-tight text-copy-label">
                  No QR
                  <br />
                  yet
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-[12.5px] leading-relaxed text-copy-muted">
                  Screenshot the QR from your bank or UPI app and upload it here.
                  Members scan it straight off the payment link.
                </p>
                <div className="mt-2.5 flex gap-2">
                  <Button tone="p" small onClick={() => void pickQr()}>
                    {qr ? 'Replace' : 'Upload QR'}
                  </Button>
                  {qr && (
                    <Button tone="g" small onClick={() => setQr(null)}>
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Hint>
            Anyone holding a payment link can see this. Put the club&rsquo;s receiving
            account here — never a personal one.
          </Hint>
        </>
      )}

      <FormError>{error}</FormError>

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Cancel
        </Button>
        <Button tone="p" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </SheetActions>
    </>
  );
}
